import React, { useCallback, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import { MdModeEdit, MdOutlineRemoveRedEye } from "react-icons/md";
import { toast } from "react-toastify";
import * as expenseService from "../../services/expenseService";
import { notifyExpenseBranchesChanged } from "../../hooks/useExpenseBranches";
import SlidePanel from "../../components/SlidePanel/SlidePanel";
import AppDropdown from "../../components/AppDropdown/AppDropdown";
import Loader from "../../components/Loader/Loader";
import IndustryGate from "./IndustryGate";
import ExpenseCenterForm, { CENTER_FORM_ID } from "./ExpenseCenterForm";
import { thStyle, tdStyle, cardStyle, iconBtnStyle } from "./expenseStyles";

const COLUMNS = ["Sl. No", "Name", "Legal Entity", "TRN", "Invoice Prefix", "Active", "Actions"];

function ExpenseManagementSettingsContent() {
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  // panel: null | { mode: "add" | "edit" | "view", center }
  const [panel, setPanel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const loadCenters = useCallback(async () => {
    try {
      setLoading(true);
      const res = await expenseService.getCenters();
      setCenters(res?.success && Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err.message || "Could not load branches");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCenters();
  }, [loadCenters]);

  const closePanel = () => {
    if (!saving) setPanel(null);
  };

  const handleSaved = () => {
    setPanel(null);
    loadCenters();
    notifyExpenseBranchesChanged(); // refresh the per-branch menus
  };

  const handleToggle = async (center) => {
    const next = center.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      setTogglingId(center.id);
      const res = await expenseService.setCenterStatus(center.id, next);
      if (res?.success) {
        setCenters((prev) => prev.map((c) => (c.id === center.id ? { ...c, status: next } : c)));
        notifyExpenseBranchesChanged();
        toast.success(res.message);
      } else {
        toast.error(expenseService.responseMessage(res));
      }
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setTogglingId(null);
    }
  };

  const filtered = centers.filter((c) =>
    statusFilter === "all" ? true : statusFilter === "active" ? c.status === "ACTIVE" : c.status === "INACTIVE"
  );

  const panelTitle =
    panel?.mode === "add" ? "Add Branch" : panel?.mode === "edit" ? "Edit Branch" : "Branch";

  const panelFooter =
    panel?.mode === "view" ? (
      <>
        <button className="btn btn-outline-secondary px-4" onClick={closePanel}>
          Close
        </button>
        <button className="btn Button primaryBackground text-white px-4" onClick={() => setPanel({ mode: "edit", center: panel.center })}>
          Edit
        </button>
      </>
    ) : panel ? (
      <>
        <button className="btn btn-outline-secondary px-4" onClick={closePanel} disabled={saving}>
          Cancel
        </button>
        <button type="submit" form={CENTER_FORM_ID} className="btn Button primaryBackground text-white px-4" disabled={saving}>
          {saving ? <Loader style={{ width: 40, height: 20 }} /> : "Save"}
        </button>
      </>
    ) : null;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
        <div>
          <h5 className="mb-1 fw-bold" style={{ color: "#111827" }}>
            Branch Setup
          </h5>
          <p className="mb-0" style={{ fontSize: "13px", color: "#6B7280" }}>
            Set up the branches you upload jobs and raise invoices for.
          </p>
        </div>
        <button
          onClick={() => setPanel({ mode: "add", center: null })}
          className="btn btn-danger"
          style={{ borderRadius: "8px", fontSize: "13px", fontWeight: 600, height: "38px" }}
        >
          + Add Branch
        </button>
      </div>

      <div className="d-flex gap-2 mb-3 align-items-center flex-wrap">
        <div style={{ width: "160px" }}>
          <AppDropdown
            value={statusFilter}
            options={[
              { label: "All", value: "all" },
              { label: "Active", value: "active" },
              { label: "Inactive", value: "inactive" },
            ]}
            onChange={(e) => setStatusFilter(e.value)}
            style={{ height: "38px", fontSize: "13px" }}
          />
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {COLUMNS.map((h) => (
                  <th key={h} style={thStyle}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array(5)
                  .fill(null)
                  .map((_, i) => (
                    <tr key={i}>
                      {COLUMNS.map((c) => (
                        <td key={c} style={tdStyle}>
                          <Skeleton width="80%" />
                        </td>
                      ))}
                    </tr>
                  ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} style={{ ...tdStyle, textAlign: "center", color: "#6B7280", padding: "48px" }}>
                    {centers.length === 0
                      ? "No branches yet. Add one to start uploading jobs."
                      : "No branches match this filter"}
                  </td>
                </tr>
              ) : (
                filtered.map((c, idx) => (
                  <tr
                    key={c.id}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#F9FAFB")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    style={{ transition: "background 0.15s" }}
                  >
                    <td style={tdStyle}>{idx + 1}</td>
                    <td style={tdStyle}>
                      <div className="d-flex align-items-center gap-2">
                        {c.logo_url && (
                          <img src={c.logo_url} alt="" style={{ height: "28px", maxWidth: "48px", objectFit: "contain" }} />
                        )}
                        <span style={{ fontWeight: 500 }}>{c.name}</span>
                      </div>
                    </td>
                    <td style={tdStyle}>{c.legal_entity_name || "—"}</td>
                    <td style={tdStyle}>{c.trn || "—"}</td>
                    <td style={tdStyle}>{c.invoice_prefix || "—"}</td>
                    <td style={tdStyle}>
                      <Form.Check
                        type="switch"
                        checked={c.status === "ACTIVE"}
                        disabled={togglingId === c.id}
                        onChange={() => handleToggle(c)}
                        id={`expense-center-switch-${c.id}`}
                      />
                    </td>
                    <td style={tdStyle}>
                      <button style={iconBtnStyle} title="View" onClick={() => setPanel({ mode: "view", center: c })}>
                        <MdOutlineRemoveRedEye size={18} />
                      </button>
                      <button style={iconBtnStyle} title="Edit" onClick={() => setPanel({ mode: "edit", center: c })}>
                        <MdModeEdit size={17} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <SlidePanel show={!!panel} onHide={closePanel} title={panelTitle} footer={panelFooter}>
        {panel && (
          <ExpenseCenterForm
            key={`${panel.mode}-${panel.center?.id || "new"}`}
            mode={panel.mode}
            center={panel.center}
            onSaved={handleSaved}
            onSavingChange={setSaving}
          />
        )}
      </SlidePanel>
    </div>
  );
}

function ExpenseManagementSettings() {
  return (
    <IndustryGate>
      <ExpenseManagementSettingsContent />
    </IndustryGate>
  );
}

export default ExpenseManagementSettings;
